// src/pages/rankings/ClubRankings.tsx
import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../../hooks/useTranslation";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import PublicHeader from "../../components/PublicHeader";
import { getPublicClubPage } from "../../firebase/publicDirectory";
import { C, FONT } from "../../theme";
import { getClubRankingSnapshot, ClubRankingEntry } from "../../firebase/rankings";

// ── Responsive hook ──────────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

const CURRENT_YEAR = new Date().getFullYear();
// Preserve access to every locally supported historical season. A new year may
// legitimately be empty; selecting it never deletes or recalculates older data.
const YEARS = Array.from(
  { length: Math.max(1, CURRENT_YEAR - 2024 + 1) },
  (_, index) => CURRENT_YEAR - index
);

// ── Icons ────────────────────────────────────────────────────────────────────
const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const EventsIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const RankingsIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);
const ClubsHeroIcon = () => (
  <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);
const TrophyEmptyIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9H4a2 2 0 0 1-2-2V5h4" /><path d="M18 9h2a2 2 0 0 0 2-2V5h-4" />
    <path d="M12 17v4" /><path d="M8 21h8" /><path d="M6 5v4a6 6 0 0 0 12 0V5H6z" />
  </svg>
);
const CalendarIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

// ── Medal / Rank Badge (matches RankingsPage pattern) ────────────────────────
function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) {
    return (
      <div style={{
        width: 34, height: 34, borderRadius: "50%",
        background: "linear-gradient(135deg, #ffd700, #f59e0b)",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 0 14px rgba(245,158,11,0.45)",
        fontSize: 13, fontWeight: 800, color: "#78350f",
        fontFamily: FONT.body, flexShrink: 0,
      }}>1</div>
    );
  }
  if (rank === 2) {
    return (
      <div style={{
        width: 34, height: 34, borderRadius: "50%",
        background: "linear-gradient(135deg, #e2e8f0, #94a3b8)",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 0 10px rgba(148,163,184,0.3)",
        fontSize: 13, fontWeight: 800, color: "var(--color-text)",
        fontFamily: FONT.body, flexShrink: 0,
      }}>2</div>
    );
  }
  if (rank === 3) {
    return (
      <div style={{
        width: 34, height: 34, borderRadius: "50%",
        background: "linear-gradient(135deg, #fb923c, #c2410c)",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 0 10px rgba(251,146,60,0.35)",
        fontSize: 13, fontWeight: 800, color: C.text,
        fontFamily: FONT.body, flexShrink: 0,
      }}>3</div>
    );
  }
  return (
    <div style={{
      width: 34, height: 34, borderRadius: "50%",
      background: C.surface2,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 12, fontWeight: 700, color: C.text2,
      fontFamily: FONT.body, flexShrink: 0,
    }}>{rank}</div>
  );
}

// ── Club avatar ───────────────────────────────────────────────────────────────
function ClubAvatar({ clubName, imageUrl, size = 40 }: { clubName: string; imageUrl?: string; size?: number }) {
  if (imageUrl) {
    return (
      <img src={imageUrl} alt="" style={{
        width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0,
        border: "2px solid var(--color-border-strong)",
      }} />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: C.surface2,
      border: "2px solid var(--color-border-strong)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: Math.round(size * 0.38), fontWeight: 700,
      color: C.text3, fontFamily: FONT.body,
    }}>
      {clubName.charAt(0).toUpperCase()}
    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
function SkeletonRow() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderBottom: `1px solid ${C.border}` }}>
      <div style={{ width: 34, height: 34, borderRadius: "50%", background: C.surface2, flexShrink: 0 }} />
      <div style={{ width: 34, height: 34, borderRadius: "50%", background: C.surface2, flexShrink: 0 }} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ height: 10, borderRadius: 6, width: "45%", background: "linear-gradient(90deg, var(--color-border) 25%, var(--color-border-strong) 50%, var(--color-border) 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
        <div style={{ height: 8, borderRadius: 6, width: "25%", background: "linear-gradient(90deg, var(--color-border) 25%, var(--color-border) 50%, var(--color-border) 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
      </div>
      <div style={{ height: 12, borderRadius: 6, width: 44, background: "rgba(218,41,28,0.15)", flexShrink: 0 }} />
    </div>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────
interface Props {
  embedded?: boolean;
}

// ── Main component ────────────────────────────────────────────────────────────
const ClubRankings: React.FC<Props> = ({ embedded = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const w = useWindowWidth();
  const isMobile = w < 640;
  const isTablet = w >= 640 && w < 1024;

  const [year, setYear] = useState(CURRENT_YEAR);
  const [clubs, setClubs] = useState<ClubRankingEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [clubImageMap, setClubImageMap] = useState<Record<string, string>>({});
  const [clubSlugMap, setClubSlugMap] = useState<Record<string, string>>({});
  const [heroLoaded, setHeroLoaded] = useState(false);

  useEffect(() => { const id = setTimeout(() => setHeroLoaded(true), 80); return () => clearTimeout(id); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const snap = await getClubRankingSnapshot(year);
      if (!snap) {
        setClubs([]);
        setNotFound(true);
      } else {
        setClubs(snap.clubs);
      }
    } catch {
      setClubs([]);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const fetchClubImages = async () => {
      try {
        const allClubs = await getPublicClubPage({ pageSize: 100 });
        const map: Record<string, string> = {};
        const slugs: Record<string, string> = {};
        allClubs.forEach((c) => { if (c.imageUrl) map[c.officialName] = c.imageUrl; slugs[c.officialName] = c.slug; });
        setClubImageMap(map);
        setClubSlugMap(slugs);
      } catch { /* ignore */ }
    };
    fetchClubImages();
  }, [year]);

  // ── Year selector ─────────────────────────────────────────────────────────
  const yearSelector = (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <CalendarIcon />
      <div style={{ display: "flex", gap: 6 }}>
        {YEARS.map((y) => (
          <button
            key={y}
            className="cr-year-btn"
            style={{
              fontFamily: FONT.body, fontSize: 12, fontWeight: 700,
              padding: "5px 14px", borderRadius: 8, cursor: "pointer",
              transition: "all 0.15s", letterSpacing: "0.3px",
              ...(year === y
                ? { background: C.red, color: C.onAccent, border: "1px solid #da291c" }
                : { background: C.surface2, color: C.text2, border: `1px solid ${C.border}` }
              ),
            }}
            onClick={() => setYear(y)}
          >
            {y}
          </button>
        ))}
      </div>
    </div>
  );

  // ── Table card (shared for embedded + standalone) ─────────────────────────
  const tableCard = (
    <div style={{ backgroundColor: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, overflow: "hidden" }}>
      {/* Table header */}
      <div style={{
        display: "flex", alignItems: "center",
        padding: isMobile ? "10px 14px" : "10px 18px",
        borderBottom: `1px solid ${C.border}`,
        backgroundColor: "var(--color-surface-2)",
        gap: 12,
      }}>
        <div style={{ width: 34, flexShrink: 0 }} />
        {!isMobile && <div style={{ width: 40, flexShrink: 0 }} />}
        <div style={{ flex: 1, fontSize: 9, fontWeight: 800, letterSpacing: "1px", color: C.text3, textTransform: "uppercase", fontFamily: FONT.body }}>
          {t("ranking.clubRankings.table.club")}
        </div>
        {!isMobile && (
          <div style={{ width: 80, fontSize: 9, fontWeight: 800, letterSpacing: "1px", color: C.text3, textTransform: "uppercase", fontFamily: FONT.body, textAlign: "center" }}>
            {t("ranking.clubRankings.table.athletes")}
          </div>
        )}
        <div style={{ width: 60, fontSize: 9, fontWeight: 800, letterSpacing: "1px", color: C.text3, textTransform: "uppercase", fontFamily: FONT.body, textAlign: "right" }}>
          {t("ranking.clubRankings.table.points")}
        </div>
      </div>

      {/* Rows — clickable; navigates to /rankings?club=NAME so the public
          ranking page can show a club-scoped subset of athletes. */}
      {clubs.map((c, idx) => {
        const isTop = c.rank <= 3;
        const publicSlug = clubSlugMap[c.clubName];
        return (
          <div
            key={c.clubName + idx}
            className={isTop ? "cr-row cr-row-top" : "cr-row"}
            role={publicSlug ? "button" : undefined}
            tabIndex={publicSlug ? 0 : -1}
            title={publicSlug ? t("publicDirectory.viewClub") : t("publicDirectory.clubs.emptyText")}
            onClick={() => publicSlug && navigate(`/clubs/${publicSlug}`)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                if (publicSlug) navigate(`/clubs/${publicSlug}`);
              }
            }}
            style={{
              display: "flex", alignItems: "center",
              padding: isMobile ? "12px 14px" : "13px 18px",
              borderBottom: `1px solid ${C.border}`,
              gap: 12, transition: "background 0.15s",
              backgroundColor: isTop ? "rgba(218,41,28,0.03)" : "transparent",
              cursor: "pointer",
              ...(c.rank === 1 ? { borderLeft: "2px solid rgba(245,158,11,0.5)" } :
                  c.rank === 2 ? { borderLeft: "2px solid rgba(148,163,184,0.4)" } :
                  c.rank === 3 ? { borderLeft: "2px solid rgba(251,146,60,0.4)" } :
                  { borderLeft: "2px solid transparent" }),
            }}
          >
            {/* Rank badge */}
            <div style={{ flexShrink: 0 }}>
              <RankBadge rank={c.rank} />
            </div>

            {/* Avatar */}
            {!isMobile && (
              <ClubAvatar clubName={c.clubName} imageUrl={clubImageMap[c.clubName]} size={36} />
            )}

            {/* Name + athlete count */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: isMobile ? 13 : 14, fontWeight: 700, color: C.text,
                fontFamily: FONT.body, lineHeight: 1.2,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {c.clubName}
              </div>
              {isMobile && (
                <div style={{ fontSize: 11, color: C.text3, fontFamily: FONT.body, marginTop: 2 }}>
                  {c.athleteCount} {t("ranking.clubRankings.table.athletes")}
                </div>
              )}
            </div>

            {/* Athletes (desktop) */}
            {!isMobile && (
              <div style={{ width: 80, textAlign: "center", flexShrink: 0 }}>
                <span style={{
                  fontSize: 12, fontWeight: 600, color: C.text3,
                  backgroundColor: C.surface2,
                  border: `1px solid ${C.border}`,
                  borderRadius: 20, padding: "2px 10px", fontFamily: FONT.body,
                }}>
                  {c.athleteCount}
                </span>
              </div>
            )}

            {/* Points */}
            <div style={{ width: 60, textAlign: "right", flexShrink: 0 }}>
              <div style={{
                fontSize: isMobile ? 17 : 20, fontWeight: 800,
                color: C.red, fontFamily: FONT.display,
                letterSpacing: "0.5px", lineHeight: 1,
              }}>
                {c.totalPoints}
              </div>
              <div style={{ fontSize: 9, color: C.text3, fontFamily: FONT.body, marginTop: 1 }}>pts</div>
            </div>
          </div>
        );
      })}
    </div>
  );

  // ── Empty state ───────────────────────────────────────────────────────────
  const emptyState = (
    <div style={{
      textAlign: "center", padding: "72px 20px",
      backgroundColor: C.surface, borderRadius: 14,
      border: `1px solid ${C.border}`,
    }}>
      <div style={{ color: "var(--color-border)", marginBottom: 14, display: "flex", justifyContent: "center" }}>
        <TrophyEmptyIcon />
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.text2, fontFamily: FONT.body, marginBottom: 6 }}>
        {t("ranking.empty")}
      </div>
      <div style={{ fontSize: 13, color: C.text3, fontFamily: FONT.body }}>
        No club ranking data for {year}
      </div>
    </div>
  );

  // ── Skeleton ──────────────────────────────────────────────────────────────
  const skeleton = (
    <div style={{ backgroundColor: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, overflow: "hidden" }}>
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => <SkeletonRow key={i} />)}
    </div>
  );

  const mainContent = loading ? skeleton : (notFound || clubs.length === 0) ? emptyState : tableCard;

  // ── Embedded mode ─────────────────────────────────────────────────────────
  if (embedded) {
    return (
      <div style={{ paddingTop: 8 }}>
        <div style={{ marginBottom: 16 }}>{yearSelector}</div>
        {mainContent}
        <div style={{ marginTop: 12, fontSize: 11, color: C.text3, textAlign: "center", fontFamily: FONT.body, fontStyle: "italic" }}>
          {t("ranking.clubRankings.subtitle")}
        </div>
      </div>
    );
  }

  // ── Standalone page ───────────────────────────────────────────────────────
  return (
    <>
    <Helmet>
      <title>{t("seo.clubRankings.title")}</title>
      <meta name="description" content={t("seo.clubRankings.description")} />
      <link rel="canonical" href="https://registration.taekwondo.ge/rankings/clubs" />
      <meta property="og:title" content={t("seo.clubRankings.title")} />
      <meta property="og:description" content={t("seo.clubRankings.description")} />
      <meta property="og:url" content="https://registration.taekwondo.ge/rankings/clubs" />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://registration.taekwondo.ge/geo.png" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={t("seo.clubRankings.title")} />
      <meta name="twitter:description" content={t("seo.clubRankings.description")} />
      <meta name="twitter:image" content="https://registration.taekwondo.ge/geo.png" />
      <link rel="alternate" hrefLang="en" href="https://registration.taekwondo.ge/rankings/clubs" />
      <link rel="alternate" hrefLang="ka" href="https://registration.taekwondo.ge/rankings/clubs" />
      <link rel="alternate" hrefLang="x-default" href="https://registration.taekwondo.ge/rankings/clubs" />
    </Helmet>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Figtree:wght@400;500;600;700;800&display=swap');

        @keyframes heroBgZoom {
          from { transform: scale(1.07); }
          to   { transform: scale(1); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes shimmer {
          0%   { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        .cr-loaded .cr-a0 { animation: fadeUp 0.65s cubic-bezier(.22,1,.36,1) 0.05s both; }
        .cr-loaded .cr-a1 { animation: fadeUp 0.65s cubic-bezier(.22,1,.36,1) 0.15s both; }
        .cr-loaded .cr-a2 { animation: fadeUp 0.65s cubic-bezier(.22,1,.36,1) 0.25s both; }
        .cr-loaded .cr-a3 { animation: fadeUp 0.65s cubic-bezier(.22,1,.36,1) 0.35s both; }

        .cr-back:hover       { color: var(--color-text) !important; border-color: var(--color-text-subtle) !important; }
        .cr-nav-link:hover   { color: var(--color-accent) !important; }
        .cr-nav-cta:hover    { background: rgba(218,41,28,0.85) !important; }
        .cr-row:hover        { background: var(--color-surface-2) !important; }
        .cr-row-top:hover    { background: rgba(218,41,28,0.06) !important; }
        .cr-year-btn:hover   { border-color: var(--color-text-subtle) !important; }

        * { box-sizing: border-box; }
      `}</style>

      <div style={{ minHeight: "100vh", backgroundColor: C.bg, fontFamily: FONT.body }}>

        {/* ══════════ NAVBAR ════════════════════════════════════════════════ */}
        <PublicHeader backTo="/rankings" backLabel={t("nav.backToRankings")} />
        {false && <header style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          height: 56, padding: isMobile ? "0 14px" : "0 32px",
          backgroundColor: "rgba(7,10,15,0.95)",
          backdropFilter: "blur(12px)",
          borderBottom: `1px solid ${C.border}`,
          position: "sticky", top: 0, zIndex: 100,
        }}>
          <button
            className="cr-back"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "none", border: `1px solid ${C.border2}`,
              borderRadius: 8, padding: "5px 11px", cursor: "pointer",
              color: C.text2, fontFamily: FONT.body,
              fontSize: 12, fontWeight: 600, letterSpacing: "0.4px",
              transition: "color 0.15s, border-color 0.15s",
            }}
            onClick={() => navigate("/rankings")}
          >
            <BackIcon />
            {!isMobile && t("common.back")}
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/geo.png" alt="Logo" style={{ width: 30, height: 30, objectFit: "contain" }} />
            {!isMobile && (
              <div>
                <div style={{ fontFamily: FONT.display, fontSize: 14, letterSpacing: "3px", color: C.text, lineHeight: 1.1 }}>GEORGIAN TAEKWONDO</div>
                <div style={{ fontFamily: FONT.display, fontSize: 9, letterSpacing: "2.5px", color: C.text3, lineHeight: 1 }}>NATIONAL FEDERATION</div>
              </div>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 10 }}>
            <button
              className="cr-nav-link"
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                background: "none", border: "none", cursor: "pointer",
                color: C.text2, fontSize: 12, fontWeight: 600,
                fontFamily: FONT.body, letterSpacing: "0.3px", transition: "color 0.15s", padding: "4px 6px",
              }}
              onClick={() => navigate("/events")}
            >
              <EventsIcon />
              {!isMobile && <span>{t("landing.events")}</span>}
            </button>
            <button
              className="cr-nav-link"
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                background: "none", border: "none", cursor: "pointer",
                color: C.text2, fontSize: 12, fontWeight: 600,
                fontFamily: FONT.body, letterSpacing: "0.3px", transition: "color 0.15s", padding: "4px 6px",
              }}
              onClick={() => navigate("/rankings")}
            >
              <RankingsIcon />
              {!isMobile && <span>{t("ranking.pageTitle")}</span>}
            </button>
            <LanguageSwitcher />
            <button
              className="cr-nav-cta"
              style={{
                backgroundColor: C.red, color: C.onAccent,
                border: "none", borderRadius: 8, cursor: "pointer",
                fontFamily: FONT.body, fontSize: 12, fontWeight: 700,
                padding: isMobile ? "7px 12px" : "7px 16px",
                letterSpacing: "0.3px", transition: "background 0.15s",
              }}
              onClick={() => navigate("/login")}
            >
              {t("login.button")} →
            </button>
          </div>
        </header>}

        {/* ══════════ HERO ════════════════════════════════════════════════════ */}
        <div className={heroLoaded ? "cr-loaded" : ""}>
          <div style={{ position: "relative", overflow: "hidden" }}>
            {/* BG layers */}
            <div style={{
              position: "absolute", inset: 0,
              background: "linear-gradient(135deg, var(--color-bg) 0%, var(--color-surface-2) 50%, var(--color-surface) 100%)",
            }} />
            <div style={{
              position: "absolute", inset: 0,
              backgroundImage: "url('/hero-bg.jpg')",
              backgroundSize: "cover", backgroundPosition: "center",
              opacity: 0.05,
              animation: heroLoaded ? "heroBgZoom 1.8s cubic-bezier(.22,1,.36,1) forwards" : "none",
            }} />
            {/* Red vignette bottom-left */}
            <div style={{
              position: "absolute", bottom: -60, left: -80,
              width: 400, height: 400, borderRadius: "50%",
              background: "radial-gradient(circle, rgba(218,41,28,0.12) 0%, transparent 70%)",
            }} />
            {/* Bottom fade */}
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 80, background: "linear-gradient(to bottom, transparent, var(--color-bg))" }} />

            <div style={{
              position: "relative", zIndex: 2,
              maxWidth: 1280, margin: "0 auto",
              padding: isMobile ? "48px 20px 56px" : isTablet ? "56px 48px 64px" : "64px 80px 72px",
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "center" : "flex-end",
              justifyContent: "space-between",
              gap: 28,
              textAlign: isMobile ? "center" : "left",
            }}>
              <div>
                <div className="cr-a0" style={{
                  fontSize: 10, fontWeight: 800, letterSpacing: "2.5px",
                  color: C.red, textTransform: "uppercase", fontFamily: FONT.body,
                  marginBottom: 14,
                }}>
                  {t("ranking.subtitle")}
                </div>
                <h1 className="cr-a1" style={{
                  fontFamily: FONT.display, color: C.text,
                  fontSize: isMobile ? "clamp(44px,13vw,64px)" : isTablet ? "clamp(54px,8vw,76px)" : "clamp(64px,7vw,92px)",
                  letterSpacing: "2px", lineHeight: 0.95, margin: 0, marginBottom: 12,
                }}>
                  {t("ranking.clubRankings.title")}
                </h1>
                <p className="cr-a2" style={{
                  fontSize: 14, color: C.text2, fontFamily: FONT.body,
                  lineHeight: 1.55, margin: 0, maxWidth: isMobile ? "100%" : 420,
                }}>
                  {t("ranking.clubRankings.subtitle")}
                </p>

                {/* Quick pills */}
                <div className="cr-a3" style={{ display: "flex", gap: 10, marginTop: 20, flexWrap: "wrap", justifyContent: isMobile ? "center" : "flex-start" }}>
                  <button
                    className="cr-nav-link"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 7,
                      background: C.surface2, border: `1px solid ${C.border}`,
                      borderRadius: 8, padding: "7px 14px", cursor: "pointer",
                      color: C.text2, fontSize: 12, fontWeight: 600,
                      fontFamily: FONT.body, transition: "background 0.15s",
                    }}
                    onClick={() => navigate("/events")}
                  >
                    <EventsIcon />
                    <span>{t("landing.events")}</span>
                  </button>
                  <button
                    className="cr-nav-link"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 7,
                      background: C.surface2, border: `1px solid ${C.border}`,
                      borderRadius: 8, padding: "7px 14px", cursor: "pointer",
                      color: C.text2, fontSize: 12, fontWeight: 600,
                      fontFamily: FONT.body, transition: "background 0.15s",
                    }}
                    onClick={() => navigate("/rankings")}
                  >
                    <RankingsIcon />
                    <span>{t("ranking.pageTitle")}</span>
                  </button>
                </div>
              </div>

              {/* Hero icon */}
              {!isMobile && (
                <div className="cr-a1" style={{ color: "var(--color-border)", flexShrink: 0 }}>
                  <ClubsHeroIcon />
                </div>
              )}
            </div>
          </div>

          {/* ══════════ STATS BAR ══════════════════════════════════════════ */}
          {!loading && clubs.length > 0 && (
            <div style={{
              backgroundColor: C.surface2,
              borderBottom: "1px solid var(--color-border)",
            }}>
              <div style={{
                maxWidth: 1280, margin: "0 auto",
                padding: isMobile ? "16px 20px" : "18px 80px",
                display: "flex", alignItems: "center", gap: isMobile ? 20 : 40,
                flexWrap: "wrap",
              }}>
                {[
                  { label: t("ranking.clubRankings.clubsRanked"), value: clubs.length },
                  { label: t("ranking.clubRankings.totalAthletes"), value: clubs.reduce((s, c) => s + c.athleteCount, 0) },
                  { label: t("ranking.clubRankings.totalPoints"), value: clubs.reduce((s, c) => s + c.totalPoints, 0).toLocaleString() },
                  { label: t("ranking.clubRankings.season"), value: year },
                ].map((stat) => (
                  <div key={stat.label} style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontFamily: FONT.display, fontSize: 24, letterSpacing: "1px", color: C.red, lineHeight: 1 }}>
                      {stat.value}
                    </span>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "1px", color: C.text3, textTransform: "uppercase", fontFamily: FONT.body, marginTop: 2 }}>
                      {stat.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ══════════ MAIN CONTENT ════════════════════════════════════════ */}
          <div style={{
            maxWidth: 1000, margin: "0 auto",
            padding: isMobile ? "24px 14px 64px" : "32px 40px 72px",
          }}>
            {/* Filter row */}
            <div className="cr-a2" style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginBottom: 20, flexWrap: "wrap", gap: 12,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 3, height: 18, borderRadius: 2, background: "linear-gradient(180deg, #da291c, rgba(218,41,28,0.3))", flexShrink: 0 }} />
                <span style={{ fontFamily: FONT.display, fontSize: 22, letterSpacing: "1px", color: C.text }}>
                  {t("ranking.clubRankings.table.rank")} — {year}
                </span>
                {!loading && clubs.length > 0 && (
                  <span style={{
                    marginLeft: 6, fontSize: 11, fontWeight: 700,
                    color: C.text3, backgroundColor: C.surface2,
                    border: `1px solid ${C.border}`, borderRadius: 20,
                    padding: "2px 10px", fontFamily: FONT.body,
                  }}>{clubs.length} clubs</span>
                )}
              </div>
              {yearSelector}
            </div>

            {/* Table */}
            <div className="cr-a3">
              {mainContent}
            </div>

            {/* Footnote */}
            {!loading && clubs.length > 0 && (
              <div style={{ marginTop: 14, fontSize: 11, color: C.text3, textAlign: "center", fontFamily: FONT.body, fontStyle: "italic" }}>
                {t("ranking.clubRankings.subtitle")}
              </div>
            )}
          </div>

          {/* ══════════ FOOTER ══════════════════════════════════════════════ */}
          <footer style={{
            backgroundColor: C.bg,
            borderTop: `1px solid ${C.border}`,
            padding: isMobile ? "20px 20px" : "20px 40px",
          }}>
            <div style={{
              maxWidth: 1280, margin: "0 auto",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              flexWrap: "wrap", gap: 12,
            }}>
              <span style={{ fontSize: 11, color: C.text3, fontFamily: FONT.body }}>
                © {new Date().getFullYear()} Georgian Taekwondo Federation
              </span>
              <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                <button onClick={() => navigate("/rankings")} style={{ background: "none", border: "none", color: C.text3, fontSize: 11, cursor: "pointer", fontFamily: FONT.body, padding: 0 }}>
                  Rankings
                </button>
                <span style={{ color: "var(--color-border-strong)", fontSize: 11 }}>·</span>
                <button onClick={() => navigate("/events")} style={{ background: "none", border: "none", color: C.text3, fontSize: 11, cursor: "pointer", fontFamily: FONT.body, padding: 0 }}>
                  {t("landing.events")}
                </button>
                <span style={{ color: "var(--color-border-strong)", fontSize: 11 }}>·</span>
                <button onClick={() => navigate("/privacy")} style={{ background: "none", border: "none", color: C.text3, fontSize: 11, cursor: "pointer", fontFamily: FONT.body, padding: 0 }}>
                  Privacy
                </button>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </>
  );
};

export default ClubRankings;
